import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { OrderEventPayload, OrderQueueItem, OrderStatus } from '@cuito/types';
import { freshnessOf, type DataFreshness } from '../data/dataAsOf';
import {
  applyOrderEvent,
  initialState,
  mergeOrderPayloads,
  mergeSnapshot,
  pruneClosedOrders,
  type OrderStreamState,
  type StreamLevel,
} from './orderQueue';
import type { CashierSocket, OrderEvent, SocketHandlers } from './socket';

/** 轮询间隔与纯扫码模式下的探测间隔（§19.5：Level 2 每 3 秒；Level 3 每 15 秒探测一次网络） */
export const POLL_INTERVAL_MS = 3000;
export const MANUAL_RETRY_MS = 15000;

/** 连续失败多少次判定「接单面板不可信」（§19.5 Level 3） */
export const POLL_FAILURE_LIMIT = 3;

/**
 * `join` 发出后等回执的上限（A20：服务端拒绝 `join` 时**直接断开**，不发错误事件）。
 *
 * 断开固然会让我们落到轮询，但「既没断开、也没回执」同样必须被当成未订阅 ——
 * 否则徽标会一直说「实时」而永远收不到新单（没有断开、没有错误、没有日志，
 * 正是本仓反复登记的那类「失败模式是安静」）。
 */
export const JOIN_TIMEOUT_MS = 3000;

/**
 * **连接预算**：socket 发出后多久还没「连上 / 断开 / 报错」就认定实时通道不可用（§19.5）。
 *
 * 与 `JOIN_TIMEOUT_MS` 是两个不同的窗口，别合并：`JOIN_TIMEOUT_MS` 只在 `onConnect`
 * **之后**才开始计时，而 Task 24 的真浏览器验收实测到的是**连 `onConnect` 都永远不来**
 * （WS 握手被反代挡掉、HTTP 也被拦）—— 那时 `JOIN_TIMEOUT_MS` 一次都不会触发，
 * 徽标从头到尾写「实时」，面板是一块永远收不到单的砖（实测 14 秒内 0 请求、0 降级）。
 *
 * 5 秒的取法：正常同源握手在 1 秒内完成（服务端 `join` 回执另有 3 秒上限），
 * 留 5 倍余量；慢网下即使误降级，`onConnect` 一到就回到 `live`（降级是**可逆**的）。
 */
export const CONNECT_BUDGET_MS = 5000;

/** 已关闭订单的回收周期（`pruneClosedOrders` 的调用频率，远低于事件频率） */
const PRUNE_INTERVAL_MS = 60_000;

export interface OrderStreamDeps {
  createSocket: (handlers: SocketHandlers) => CashierSocket;
  fetchQueue: (params: {
    locationId: string;
    status?: OrderStatus;
    pageSize?: number;
  }) => Promise<OrderQueueItem[]>;
  pollIntervalMs?: number;
  manualRetryMs?: number;
  /** 连接预算（`CONNECT_BUDGET_MS`）；用例注入小值以免真的等 5 秒 */
  connectBudgetMs?: number;
}

export type OrderEventHandler = (event: OrderEvent, payload: OrderEventPayload) => void;

export interface OrderStream {
  /**
   * 布局当前绑定的地点（`CashierLayout` 是**唯一**持有者）。
   *
   * 页面必须从这里**订阅**，不许用 `useState(() => readCashierLocationId())` 在挂载时快照：
   * 子页面先挂载、布局的 `GET /locations` 后回来（冷启动深链 `/cashier/scan?locationId=<uuid>`）
   * 时，快照读到的永远是 `null` 且再也不重读 ⇒ 短码取单连请求都发不出去
   * （Task 24 真浏览器验收缺陷 3，P0）。放在同一个流上下文里 ⇒ 一处真相、页面自动重渲染。
   */
  locationId: string | null;
  level: StreamLevel;
  orders: OrderQueueItem[];
  lastEventAt: string | null;
  /**
   * **面板上这份队列的数据有多旧**（R-06 ④、契约 §18.1）。
   *
   * `receivedAt` = 本机把一份**服务端权威队列快照**（首连/重连的补拉、轮询那一路）合并进状态的时刻；
   * 不是订单的 `createdAt`（那是「顾客什么时候下的单」），也不是 `lastEventAt`（那是最后一次
   * **增量事件**到达的时刻 —— 事件到达说明有新单，不说明整块面板与服务端对过账）。
   * 拿不到任何快照时为 `null`，而 `isStale(null, now) === true`：面板上一片空白时说它新鲜是撒谎。
   *
   * `serverTime` 恒为 `null`：`GET /orders` 的队列载荷**没有**时间戳字段（契约 §18.1 末段
   * 明确不加 —— 加了会让 Phase 4/5 冻结的逐字键集合断言变红），本机接收时刻本来就只由本机产生。
   */
  freshness: DataFreshness | null;
  /** 订阅单个事件（详情页靠它把 `order:paid-confirmed` 落到徽标上）；返回取消订阅 */
  onOrderEvent: (handler: OrderEventHandler) => () => void;
}

/**
 * 连接状态机（`live` → `polling` → `manual`）与事件合并（§19.5）。
 *
 * 三级降级在本 Phase **只做在线退化**：轮询与纯扫码都会真的发请求、失败就如实报错，
 * **不排队**（离线队列归 Phase 9，见计划 §1.2）。`manual` 的含义因此是
 * 「接单面板不可信，请用扫码/短码取单」，而不是「本地照样能改单」。
 *
 * 定时器用**自排链**（`setTimeout` 每次重排）而不是固定周期的 `setInterval`：
 * Level 3 的探测间隔（`manualRetryMs`）与恢复后的间隔（`pollIntervalMs`）不同，
 * 固定周期要么让 `manualRetryMs` 变成死参数，要么得在回调里嵌一层「换表」逻辑。
 */
export function useOrderStream(locationId: string | null, deps: OrderStreamDeps): OrderStream {
  const {
    createSocket,
    fetchQueue,
    pollIntervalMs = POLL_INTERVAL_MS,
    manualRetryMs = MANUAL_RETRY_MS,
    connectBudgetMs = CONNECT_BUDGET_MS,
  } = deps;
  const [state, setState] = useState<OrderStreamState>(() => initialState('live'));
  /**
   * 数据时间戳（R-06 ④）：**本机接收一份服务端权威队列快照的时刻**。
   *
   * 刻意**不放进** `OrderStreamState`（`orderQueue.ts` 的纯状态层）：那个形状被
   * `initialState` / `applyOrderEvent` / `mergeSnapshot` 一串纯函数与它们的用例逐字钉着，
   * 一个「与队列内容无关的墙上时间」塞进去只会让那些纯函数变成时间相关的。
   */
  const [freshness, setFreshness] = useState<DataFreshness | null>(null);
  const stateRef = useRef(state);
  const subscribersRef = useRef(new Set<OrderEventHandler>());
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  /** 订阅事件（引用稳定：详情页的 effect 依赖它，不能每次渲染都换） */
  const onOrderEvent = useCallback((handler: OrderEventHandler) => {
    subscribersRef.current.add(handler);
    return () => {
      subscribersRef.current.delete(handler);
    };
  }, []);

  /**
   * 换地点时清空本地队列。
   *
   * 房间从 `location:<旧>` 换到 `location:<新>`，而旧地点的卡片在业务上**已经不属于本机**：
   * 留着它们，店员会照着面板去挑一张不在本店的单（所有写操作都会 404/403）。
   * `level` 一并回到 `live`，等新连接把真实状态带回来。
   *
   * ⚠️ 必须声明在下面的连接 effect **之前**：React 按声明顺序跑 effect，
   * 先清空再连接；写在后面会在新连接开始收集事件之后才把状态抹掉。
   */
  useEffect(() => {
    setState(initialState('live'));
    // 换了地点：上一份队列的接收时刻**不再代表**新地点的数据新鲜度（新快照一到就重写它）
    setFreshness(null);
  }, [locationId]);

  useEffect(() => {
    if (locationId === null) return;
    let cancelled = false;
    let socket: CashierSocket | null = null;
    let failures = 0;
    let joinTimer: ReturnType<typeof setTimeout> | null = null;
    let budgetTimer: ReturnType<typeof setTimeout> | null = null;
    /**
     * 与 `state.level` 同步维护的本地副本。
     *
     * 定时器回调里要读「现在」的级别，而 `setState` 是异步的（同一 tick 里读 `state.level`
     * 会读到上一次渲染的值）—— `manual` 的 15 秒探测间隔就靠它生效。
     */
    let level: StreamLevel = 'live';
    /**
     * 服务端已回执 `join`（订阅成立 ⇒ 房间内的增量可信）。
     *
     * 只有它为真时，一次**成功的快照补齐**才允许把级别拉回 `live`（见 `poll`）：
     * 「补拉失败」降级之后，WS 并没有断，没必要在 `polling` 里空转到下次重连。
     */
    let subscribed = false;
    /**
     * 正在途中的那次快照请求期间，从 WS 到达的订单 id。
     *
     * 快照查询发出**之后**才出现的单必然不在快照里，而 `mergeSnapshot` 的语义是
     * 「快照 = PENDING 全集」—— 照搬它就会把刚刚推到手的新单删掉（又一种漏单）。
     */
    let arrivedDuringSync: Set<string> | null = null;

    const applyLevel = (next: StreamLevel): void => {
      level = next;
      setState((current) => (current.level === next ? current : { ...current, level: next }));
    };

    const stopPolling = (): void => {
      if (pollTimerRef.current !== null) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    const clearJoinTimer = (): void => {
      if (joinTimer !== null) {
        clearTimeout(joinTimer);
        joinTimer = null;
      }
    };

    const clearBudgetTimer = (): void => {
      if (budgetTimer !== null) {
        clearTimeout(budgetTimer);
        budgetTimer = null;
      }
    };

    /**
     * 实时通道不可用 ⇒ 降级轮询（§19.5 Level 2）。**只在 `live` 上生效**：
     *   · `connect_error` 会随 engine 的重试反复到达（默认 1~5 秒一次），若每次都重排轮询表，
     *     3 秒的间隔就永远等不到 —— 定时器被一次次推后，看起来「降级了」，实际一个请求都不发；
     *   · 已经 `manual`（连续 3 次轮询失败）时也不该被一次 `connect_error` 拉回轮询，
     *     否则 15 秒的探测间隔会被打回 3 秒，省电与减负两头落空。
     */
    const degradeToPolling = (): void => {
      if (cancelled || level !== 'live') return;
      applyLevel('polling');
      startPolling();
    };

    const scheduleNextPoll = (): void => {
      if (cancelled) return;
      const period = level === 'manual' ? manualRetryMs : pollIntervalMs;
      pollTimerRef.current = setTimeout(() => {
        void poll();
      }, period);
    };

    const poll = async (): Promise<void> => {
      let reschedule = true;
      try {
        await syncQueue();
        if (cancelled) return;
        failures = 0;
        // §19.5 的恢复顺序是「先回到 Level 2，再回到 Level 1」：网络一恢复就退出纯扫码，
        // 而不是继续每 15 秒才看一次（WS 自己的重连会把它带回 Level 1）
        if (level === 'manual') applyLevel('polling');
        // 订阅还活着（`joined` 未失效）⇒ 快照一补齐就回到实时，并且**停表**：
        // 这条只服务于「补拉失败」那类降级 —— 那时 WS 好端端的，没有理由在轮询里空转到下次重连。
        if (subscribed && level !== 'live') {
          stopPolling();
          applyLevel('live');
          reschedule = false;
        }
      } catch {
        if (cancelled) return;
        failures += 1;
        // 连续 3 次失败 ⇒ Level 3（纯扫码）。此时**不**停表：改成 manualRetryMs 探测一次，
        // 网络恢复能自己回到 Level 2（§19.5 的「恢复 → 先进 Level 2，再进 Level 1」）。
        if (failures >= POLL_FAILURE_LIMIT) applyLevel('manual');
      } finally {
        if (reschedule) scheduleNextPoll();
      }
    };

    const startPolling = (): void => {
      stopPolling();
      scheduleNextPoll();
    };

    /**
     * 把一次快照合并进队列，并**保护补拉期间从 WS 到达的单**。
     *
     * `mergeSnapshot` 的语义是「这次快照代表这些 PENDING 单的全集」，删掉不在快照里的本地
     * PENDING 是对的（被别人接走 / 已取消）。唯一的例外是**在途窗口**：快照的查询发生在
     * 发请求那一刻，而它回来之前推进来的新单**必然**不在里面 —— 那不是「已经不是待接单」，
     * 那是「快照根本没见过它」。不设这道闸，补拉本身就会吃掉一张刚到的单（漏单换了个形状）。
     */
    const mergeFetched = (items: readonly OrderQueueItem[], arrived: ReadonlySet<string>): void => {
      // ★ R-06 ④：**接收时刻**在数据真正到手的那一行记录，晚于它的一律不算（见 `OrderStream.freshness`）
      setFreshness(freshnessOf(new Date(), null));
      setState((current) => {
        const merged = mergeSnapshot(current, items);
        let orders = merged.orders;
        for (const orderId of arrived) {
          const local = current.orders[orderId];
          if (local === undefined || orders[orderId] !== undefined) continue;
          if (orders === merged.orders) orders = { ...merged.orders };
          orders[orderId] = local;
        }
        return orders === merged.orders ? merged : { ...merged, orders };
      });
    };

    /**
     * 拉一次 `GET /orders?locationId=…&status=PENDING` 快照并合并（契约 §12.2，Task 5 已交付）。
     *
     * **首连/重连的补拉与定时轮询共用这一条路径**（缺陷 1 的最小修法 ①）：
     * 首连的 `join` 不带 `lastEventAt` ⇒ 服务端只回 `pendingOrderCount`、**不发**
     * `changedOrders`（`orders.gateway.ts:235`），所以「打开面板看到既有待接单」这件事
     * 只能由客户端自己补拉 —— 路线图风险登记里那条「重连后主动补拉」的缓解条款，
     * 此前一行都没有落地。两条通道各写一遍合并逻辑，迟早只有一条被修（§4.2 的发射纪律同理）。
     */
    const syncQueue = async (): Promise<void> => {
      const arrived = new Set<string>();
      arrivedDuringSync = arrived;
      try {
        const items = await fetchQueue({ locationId, status: 'PENDING', pageSize: 50 });
        if (cancelled) return;
        mergeFetched(items, arrived);
      } finally {
        if (arrivedDuringSync === arrived) arrivedDuringSync = null;
      }
    };

    const handlers: SocketHandlers = {
      onConnect: () => {
        if (cancelled) return;
        failures = 0;
        // 连上了 ⇒ 连接预算收工；订阅要等 `joined` 回执才算成立
        clearBudgetTimer();
        stopPolling();
        clearJoinTimer();
        subscribed = false;
        applyLevel('live');
        const lastEventAt = stateRef.current.lastEventAt;
        socket?.emit('join', {
          locationId,
          ...(lastEventAt === null ? {} : { lastEventAt }),
        });
        joinTimer = setTimeout(() => {
          if (cancelled) return;
          // 3 秒没有 `joined` ⇒ 视为未订阅（A20）
          applyLevel('polling');
          startPolling();
        }, JOIN_TIMEOUT_MS);
      },
      onDisconnect: () => {
        if (cancelled) return;
        clearBudgetTimer();
        clearJoinTimer();
        subscribed = false;
        applyLevel('polling');
        startPolling();
      },
      onConnectError: () => {
        if (cancelled) return;
        // 传输层已经明确说连不上：没有理由再等满预算。
        // （`error.message` 原文由 `socket.ts` 保留并转交，`socket.spec.ts` 在**传输边界**上钉住它；
        //   这一层只负责动作 —— 徽标改口 + 开始补拉。把原因显示到界面上是另一件事，本任务不做。）
        clearBudgetTimer();
        subscribed = false;
        degradeToPolling();
      },
      onJoined: (joined) => {
        if (cancelled) return;
        clearJoinTimer();
        clearBudgetTimer();
        subscribed = true;
        if (level !== 'live') {
          // 迟到的回执：确实订阅上了，停掉轮询回到实时（否则会一直轮询到下次重连）
          stopPolling();
          applyLevel('live');
        }
        setState((current) => mergeOrderPayloads(current, joined.changedOrders ?? []));
        // ★ 缺陷 1：回执之后**主动补拉**一次 PENDING 队列。
        // 放在 `joined` 之后而不是 `onConnect`：join 已经把本机加进了房间，快照与增量之间
        // 不会留「既不在快照里、也没被推送」的空档。
        void syncQueue().catch(() => {
          if (cancelled) return;
          // 队列没补齐 ⇒ 此刻的面板不可信，宁可徽标改口「轮询模式」也不能显示一块
          // 「空的实时面板」（3 秒后重试，成功即回到 live）。
          failures += 1;
          degradeToPolling();
        });
      },
      onOrderEvent: (event, payload) => {
        if (cancelled) return;
        // 在途快照的护住名单：这些单比快照新，不能被它删掉（见 `mergeFetched`）
        arrivedDuringSync?.add(payload.orderId);
        for (const handler of subscribersRef.current) handler(event, payload);
        setState((current) => applyOrderEvent(current, event, payload));
      },
    };

    socket = createSocket(handlers);

    /**
     * **连接预算**（缺陷 2）：socket 已经发出，`connectBudgetMs` 之内既没连上、也没断开、
     * 也没收到 `connect_error` ⇒ 认定实时通道不可用，降级轮询。
     *
     * 这一条覆盖的正是 Task 24 实测到的现场：两条传输都被拦掉时，`JOIN_TIMEOUT_MS`
     * （只在 `onConnect` 之后启动）永远不会触发，徽标从头到尾写「实时」、一个请求都不发。
     * 也**不**主动断开 socket：engine 的重连还在跑，一旦连上 `onConnect` 就把级别拉回 `live`。
     */
    budgetTimer = setTimeout(() => {
      budgetTimer = null;
      degradeToPolling();
    }, connectBudgetMs);

    // 回收已关闭的单（§4.4 的 `pruneClosedOrders`）：60 秒一次，量级远低于事件频率
    const pruneTimer = setInterval(() => {
      setState((current) => pruneClosedOrders(current));
    }, PRUNE_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearJoinTimer();
      clearBudgetTimer();
      stopPolling();
      clearInterval(pruneTimer);
      socket?.disconnect();
      socket = null;
      subscribersRef.current.clear();
    };
  }, [locationId, createSocket, fetchQueue, pollIntervalMs, manualRetryMs, connectBudgetMs]);

  const orders = useMemo(() => Object.values(state.orders), [state.orders]);

  // `locationId` 一并交出去（原样回传入参）：页面从上下文订阅它，而不是各自在挂载时快照
  return { locationId, level: state.level, orders, lastEventAt: state.lastEventAt, freshness, onOrderEvent };
}
